import dayjs from 'dayjs';
import * as yup from 'yup';
import { InferType } from 'yup';
import { InsurerType as InsurerTypeType } from '../../types';

export type InsurerType = Omit<
  InsurerTypeType,
  'eligibilityRef' | 'memberIdPrefix' | 'memberIdRequired' | '_typeName'
>;

export enum ToolRunMode {
  PREVIEW = 'preview',
  SUBMISSION = 'submission',
}

export enum EligibilitySearchDepth {
  STANDARD = 'standard',
  DEEP = 'deep',
}

export const addAllOption = <T>(data: T[]): T[] => [
  { id: -1, name: 'All', identifier: 'All' } as T,
  ...data,
];

export const ToolValidationSchema = yup.object({
  client: yup
    .object({
      id: yup.number().required(),
      identifier: yup.string().required(),
      name: yup.string().required(),
    })
    .required(),
  partnership: yup
    .object({
      id: yup.number().required(),
      partnershipType: yup.string().required(),
      name: yup.string().required(),
    })
    .nullable()
    .optional()
    .default(null),
  insurer: yup
    .object({
      id: yup.number().required(),
      identifier: yup.string().required(),
      name: yup.string().required(),
    })
    .nullable()
    .optional()
    .default(null),
  billingType: yup.string().required(),
  excludePartnership: yup.boolean().required().default(false),
  runAdminFeeClaims: yup.boolean().required().default(false),
  claimsLimit: yup.number().min(0).required().default(0),
  eligibilitySearchDepth: yup
    .string()
    .required()
    .oneOf([EligibilitySearchDepth.STANDARD, EligibilitySearchDepth.DEEP])
    .default(EligibilitySearchDepth.STANDARD),
  runMode: yup
    .string()
    .required()
    .oneOf([ToolRunMode.SUBMISSION, ToolRunMode.PREVIEW])
    .default(ToolRunMode.PREVIEW),
  paymentTypes: yup
    .array()
    .of(yup.string().required())
    .nullable()
    .defined()
    .default(null),
  startDate: yup.date().typeError('Invalid date').defined().required(),
  endDate: yup
    .date()
    .typeError('Invalid date')
    .defined()
    .required()
    .test({
      name: 'endDateBeforeStart',
      skipAbsent: true,
      test(value, ctx) {
        const startDate = ctx.parent.startDate;
        if (dayjs(value).isBefore(dayjs(startDate), 'day')) {
          return ctx.createError({
            message: 'End date is before start date',
          });
        }
        return true;
      },
    })
    .test({
      name: 'oneYearRelativeMaximum',
      skipAbsent: true,
      test(value, ctx) {
        const startDate = ctx.parent.startDate;
        if (dayjs(value).diff(dayjs(startDate), 'year') >= 1) {
          return ctx.createError({
            message: 'Date range must be within 1 year',
          });
        }
        return true;
      },
    }),
});

export type ToolFormValues = InferType<typeof ToolValidationSchema>;
